/**
 * Generated-document manifest `page` handling (#20423, MRE #30376).
 *
 * `SourceProvenanceReference.page` is `int | int[]`: a bare number for a
 * single-page chunk, a sorted list for a chunk spanning several pages
 * (`chunk_metadata.page_number` became canonically `list[int]` in MRE #24082).
 *
 * The two shapes serve different consumers, so they get one helper each:
 * display wants the whole span, navigation wants a single page to open.
 */

/** Pages as a de-duplicated, ascending list of integers. */
const toPages = (page: number | number[] | null | undefined): number[] => {
  if (Array.isArray(page)) {
    return Array.from(new Set(page.filter((p) => Number.isInteger(p)))).sort(
      (a, b) => a - b,
    );
  }
  return Number.isInteger(page) ? [page as number] : [];
};

/**
 * Human-facing page label, mirroring the backend's `_page_label` so a
 * generated document and its provenance panel read the same:
 * `47` · `47-49` (contiguous span) · `47, 52` (sparse).
 *
 * Returns null when there is no usable page, so callers can omit the suffix
 * entirely rather than rendering "p. null".
 */
export const manifestPageLabel = (
  page: number | number[] | null | undefined,
): string | null => {
  const pages = toPages(page);
  if (!pages.length) return null;
  if (pages.length === 1) return String(pages[0]);
  const contiguous = pages.every((p, i) => p === pages[0] + i);
  return contiguous
    ? `${pages[0]}-${pages[pages.length - 1]}`
    : pages.join(", ");
};

/**
 * Single page to open in the document viewer — the first of the span.
 *
 * A viewer deep link and a `DocumentAnnotation` both need one number: the
 * route reads `?page_number=` through `Number(...)`, which yields NaN for a
 * comma-joined list, and annotation placement compares `page` by equality.
 * Interpolating the raw array silently lands the reader on page 1 with no
 * highlight, so every navigation path goes through this.
 */
export const manifestPageTarget = (
  page: number | number[] | null | undefined,
): number | null => {
  const pages = toPages(page);
  return pages.length ? pages[0] : null;
};
